import { dbPost } from '../store/db.js';

// In-app bug reporting, the pattern shared by Meal Hat, Movie Hat, Cinema
// Roll and Full Table: a small always-visible button, a plain textarea, a
// write-only database node, triage from the command line (Meal Hat's
// `yarn fetch-bug-reports` — reports from here land in the same node).
//
// Reports go through the same REST layer as everything else, so a signed-in
// reporter's email rides along on the token. But the node also accepts
// SIGNED-OUT reports — deliberately: the whole front door of this app is a
// sign-in, and a report box behind it cannot hear about a sign-in that
// fails. It is doubly true here, where "I signed in and it just says I'm
// waiting" is the single most likely thing anyone reports.

/**
 * What was true when the report was filed, chosen for what would explain a
 * bug in THIS app: who they are, which hat the course is attached to, and
 * what they were doing (planning, cooking, the debrief). `app` is how Meal
 * Hat's fetch-bug-reports tells these reports from its own — both land in
 * the same node.
 */
function buildAppStateSummary (store, route) {
  const state = store.state;

  return {
    route: route?.fullPath || window.location.hash,
    version: process.env.VUE_APP_VERSION || null,

    app: 'enjoy-cooking',
    signedIn: Boolean(state.email),
    hat: state.hat,
    activity: state.activity,

    appError: state.appError,
    standalone: window.matchMedia?.('(display-mode: standalone)')?.matches || false,
    online: navigator.onLine
  };
}

function buildReport (store, text, route) {
  return {
    createdAt: { '.sv': 'timestamp' },
    transcript: text,
    reporterEmail: store.state.email || null,
    url: window.location.href,
    userAgent: navigator.userAgent,
    screenSize: `${window.innerWidth}x${window.innerHeight}`,
    devicePixelRatio: window.devicePixelRatio || 1,
    // Stringified, not a nested object: RTDB silently drops keys whose value
    // is an empty object, so a plain object here can lose fields unnoticed.
    appState: JSON.stringify(buildAppStateSummary(store, route))
  };
}

// Offline stash: a report typed with no signal waits in localStorage and
// goes out on the next launch or the next successful send.
const STASH_KEY = 'enjoyCookingPendingBugReports';
const STASH_LIMIT = 10;

function readStash () {
  try {
    const parsed = JSON.parse(localStorage.getItem(STASH_KEY) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeStash (reports) {
  try {
    localStorage.setItem(STASH_KEY, JSON.stringify(reports.slice(-STASH_LIMIT)));
  } catch {
    // Storage full or blocked. Nothing more a best-effort stash can do.
  }
}

/** Send anything stashed earlier. Returns how many went out. */
export async function flushStashedBugReports () {
  const stash = readStash();
  if (!stash.length || !navigator.onLine) return 0;

  let sent = 0;
  for (const report of stash) {
    try {
      // Re-stamped on the eventual write; clientCreatedAt still says when it
      // actually happened.
      await dbPost('bugReports', { ...report, createdAt: { '.sv': 'timestamp' } });
      sent += 1;
    } catch {
      // Still unreachable — keep the rest for next time.
      break;
    }
  }

  writeStash(stash.slice(sent));
  return sent;
}

/**
 * File a report.
 *
 * Resolves `{ queued: true }` when it reached only the stash, so the UI can
 * say "saved, will send later" instead of a misleading "sent". Throws only
 * when online and the write genuinely failed — the text stays with the
 * caller for a retry, and stashing it too would send a duplicate.
 */
export async function submitBugReport (store, transcript, route) {
  const text = (transcript || '').trim();
  if (!text) throw new Error('Describe what happened before sending.');

  const report = buildReport(store, text, route);

  try {
    await dbPost('bugReports', report);
    flushStashedBugReports().catch(() => {});
  } catch (error) {
    if (!navigator.onLine) {
      writeStash([...readStash(), { ...report, createdAt: null, clientCreatedAt: Date.now(), queuedOffline: true }]);
      return { queued: true };
    }
    throw error;
  }

  return { queued: false };
}
